import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";
import { fetchHomePhotos } from "@/lib/api";

/**
 * Right-column "Dealer Corner" photo slider on Dealer Home — mirrors
 * VideoSidebar's layout (brand strip + hero + single-item carousel with
 * arrows + dots) in an emerald→teal theme. Photos are admin-curated (checked
 * in the Gallery) and served from `/postcue/home-photos`.
 */
export default function PhotosSidebar({ dealerSlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetchHomePhotos(dealerSlug);
        if (!cancel) setItems(res?.photos || []);
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [dealerSlug]);

  if (loading || items.length === 0) return null;

  const safeIdx = ((idx % items.length) + items.length) % items.length;
  const current = items[safeIdx];

  return (
    <div className="space-y-3" data-testid="photos-sidebar-wrap">
      {/* Emerald brand strip — same template as the other rail panels. */}
      <div className="rounded-t-2xl bg-emerald-200/70 px-5 py-3 flex items-center">
        <div className="font-semibold text-emerald-900 tracking-tight whitespace-nowrap">
          Dealer Corner
        </div>
      </div>

      {/* Emerald→teal hero. */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-700 text-white px-5 py-7 min-h-[140px] flex flex-col justify-center">
        <h2 className="text-xl font-semibold tracking-tight leading-tight flex items-center gap-2">
          <Users size={22} className="text-emerald-100 shrink-0" />
          Gallery of <span className="text-emerald-200">Professionals</span>
        </h2>
        <p className="mt-2 text-xs text-white/90 leading-relaxed">
          Meet the pros &amp; spas delivering results with OreVital.
        </p>
      </div>

      {/* Photo carousel */}
      <div
        className="rounded-2xl overflow-hidden border border-emerald-100 bg-white"
        data-testid="photos-sidebar"
      >
        <div className="p-4 relative">
          <div className="rounded-xl border border-emerald-100 overflow-hidden bg-zinc-100">
            <div className="relative aspect-[4/5] bg-zinc-100">
              <img
                key={current.url}
                src={current.url}
                alt={current.title || "Professional"}
                className={`w-full h-full object-cover ${current.slug ? "cursor-pointer" : ""}`}
                data-testid={`photos-sidebar-img-${safeIdx}`}
                onError={(e) => (e.currentTarget.style.display = "none")}
                onClick={() => {
                  if (!current.slug) return;
                  // Dealer Corner post → open that tab on the home page (no pop-ups).
                  window.dispatchEvent(new CustomEvent("postcue:open-post", { detail: current.slug }));
                  document.querySelector('[data-testid="dealer-post-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              />
              {current.slug && (
                <span className="absolute top-2 left-2 rounded-full bg-white/90 text-teal-800 text-[10px] font-semibold px-2 py-0.5">Read post →</span>
              )}
              {current.title && (
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-teal-900/75 to-transparent px-4 pt-8 pb-3 text-sm font-semibold text-white leading-snug">
                  {current.title}
                </span>
              )}
            </div>
          </div>

          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setIdx(safeIdx - 1)}
                className="absolute top-1/2 -left-1 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-emerald-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-teal-700 hover:border-teal-300"
                aria-label="Previous photo"
                data-testid="photos-sidebar-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => setIdx(safeIdx + 1)}
                className="absolute top-1/2 -right-1 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-emerald-200 shadow-md flex items-center justify-center text-zinc-600 hover:text-teal-700 hover:border-teal-300"
                aria-label="Next photo"
                data-testid="photos-sidebar-next"
              >
                <ChevronRight size={18} />
              </button>

              <div className="flex items-center justify-center gap-1.5 mt-3">
                {items.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === safeIdx ? "w-5 bg-teal-600" : "w-1.5 bg-zinc-300"
                    }`}
                    aria-label={`Go to photo ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
